import { defineComponent as _defineComponent } from 'vue'
import CcAlert from '@/views/Automation/CampaignCreatorV2/components/CcAlert/CcAlert.ts.vue';
import CcIcon from '@/views/Automation/CampaignCreatorV2/components/CcIcon/CcIcon.ts.vue';
import { ViewLevelActionsEvent } from '@/views/Automation/CampaignCreatorV2/components/CcPopover/helpers';
import CcImageCard from '@/views/Automation/CampaignCreatorV2/components/Modals/CcAddImageModal/components/CcImageCard.ts.vue';
import CcImagePlacementCrop from '@/views/Automation/CampaignCreatorV2/components/Modals/CcAddImageModal/components/CcImagePlacementCrop.ts.vue';
import CcSecondaryFilter from '@/views/Automation/CampaignCreatorV2/components/Modals/CcAddImageModal/components/CcSecondaryFilter.ts.vue';
import CcTagsFilter from '@/views/Automation/CampaignCreatorV2/components/Modals/CcAddImageModal/components/CcTagsFilter.ts.vue';
import LoadingSkeleton from '@/views/Automation/CampaignCreatorV2/components/Modals/CcAddImageModal/components/LoadingSkeleton.ts.vue';
import {
  fetchImages,
  Filter,
  FiltersType,
  getFilters,
} from '@/views/Automation/CampaignCreatorV2/components/Modals/CcAddImageModal/core';
import {
  availablePlacementAspectRatios,
  categories,
  secondaryFilters,
} from '@/views/Automation/CampaignCreatorV2/components/Modals/CcAddImageModal/helpers';
import {
  AspectRatio,
  BaseMessageType,
  CategoryType,
  EditImage,
  Image,
  Step,
} from '@/views/Automation/CampaignCreatorV2/components/Modals/CcAddImageModal/types';
import CcCropImageModal from '@/views/Automation/CampaignCreatorV2/components/Modals/CcCropImageModal.ts.vue';
import CcEditImageModal from '@/views/Automation/CampaignCreatorV2/components/Modals/CcEditImageModal.ts.vue';
import CcModal from '@/views/Automation/CampaignCreatorV2/components/Modals/CcModal.ts.vue';
import { ImageCreative, SupportedCrop } from '@/views/Automation/CampaignCreatorV2/helpers/facebook/AdCreative';
import useAdAccountStore from '@/views/Automation/CampaignCreatorV2/store/AdAccount';
import useCreativeStore from '@/views/Automation/CampaignCreatorV2/store/Creative';
import NoData from '@sh/components/MediaManager/NoData/NoData.ts.vue';
import { MediaManagerHelpers, pluralize } from '@sh/helpers';
import { api } from '@sh/services/api';
import { MediaManager, Position, Tag } from '@sh/types';
import { cloneDeep, debounce, uniqBy } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref } from 'vue';

interface Emits {
  (e: 'onAddImage', value: ImageCreative[]): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CcAddImageModal.ts',
  emits: ["onAddImage"],
  setup(__props, { expose, emit: emits }: { emit: ({
  (e: 'onAddImage', value: ImageCreative[]): void;
}), expose: any, slots: any, attrs: any }) {



const adAccountStore = useAdAccountStore();
const adAccountGetters = storeToRefs(adAccountStore);

const isModalOpen = ref(false);
const isLoadingData = ref(false);
const isImageUploading = ref(false);
const isLoadingMore = ref(false);

const hiddenInputFile = ref<NullableType<HTMLInputElement>>(null);

const activeStep = ref<Step>(Step.SELECT_MEDIA);
const search = ref('');
const availableTags = ref<Tag[]>([]);
const secondaryCategories = ref(secondaryFilters);

const addImageError = ref<string>('');

const cropImage = ref<NullableType<EditImage>>(null);
const editImage = ref<NullableType<ImageCreative>>(null);
const imagesToEdit = ref<NullableType<ImageCreative[]>>(null);

const activeCategory = ref<CategoryType>(CategoryType.ALL);

const loadingProgress = ref(0);

const data = ref(getFilters());

const creativeStore = useCreativeStore();
const creativeGetters = storeToRefs(creativeStore);

const imageIdsFromStore = computed(() => {
  const data = creativeGetters.getField.value('image')?.value;
  return data.map((item) => item.value.id);
});

const availableCategories = computed(() =>
  categories.filter((item) =>
    Object.values(data.value).find((data) => data.category === item.category && (data.images.length || data.visible))
  )
);

const images = computed(() =>
  uniqBy(
    imagesToEdit.value?.map<Image>((item) => ({ ...item, selected: true, category: CategoryType.ALL })) ??
      data.value[activeCategory.value]?.images ??
      [],
    'id'
  )
);

const selectedImages = computed(() => images.value.filter((image) => image.selected));

function getPlacementKey(image: Image, aspectRatio: AspectRatio) {
  return `${image.id}-${image.image_url}-${aspectRatio.value}-${
    cropImage.value?.id === image.id && cropImage.value?.aspectRatio.value === aspectRatio.value
  }`;
}

const modalButtons = computed(() => {
  if (activeStep.value === Step.SELECT_MEDIA) {
    const disabledMessage =
      selectedImages.value.length === 0 && !isLoadingData.value ? 'Please select at least one image.' : undefined;

    return {
      save: {
        label: 'Next',
        disabledMessage,
      },
      cancel: {
        label: 'Cancel',
      },
    };
  }

  if (activeStep.value === Step.EDIT_MEDIA && !imagesToEdit.value) {
    return {
      cancel: {
        label: 'Back',
      },
    };
  }

  return {};
});

const noDataMessage = computed(() => {
  if (data.value[activeCategory.value].payload.querySearch) {
    return "We couldn't find any images that match your search.";
  }

  return 'No images were found.';
});

function initializeFilters() {
  data.value = getFilters();

  if (data.value.ACCOUNT && adAccountGetters.currentAdAccount.value) {
    data.value.ACCOUNT.payload.selectedAccounts = [adAccountGetters.currentAdAccount.value.id];
  }
}

async function onPageChange(page: number) {
  try {
    isLoadingMore.value = true;
    data.value[activeCategory.value].payload.page = page;

    const records = await data.value[activeCategory.value].getImages();
    data.value[activeCategory.value].images = [...images.value, ...records.images];
  } finally {
    isLoadingMore.value = false;
  }
}

function handleImagesScroll(event: Event) {
  const container = event.target as HTMLElement;
  const { scrollHeight, scrollTop, clientHeight } = container;

  if (
    scrollHeight - scrollTop + 100 >= clientHeight &&
    !data.value[activeCategory.value].isLoading &&
    !isLoadingMore.value &&
    data.value[activeCategory.value].totalItems > images.value.length
  ) {
    onPageChange((data.value[activeCategory.value].payload.page ?? 1) + 1);
  }
}

async function onFilterImages(promise: Promise<FiltersType>) {
  let contents: NullableType<FiltersType> = null;

  const intervalId = setInterval(() => {
    loadingProgress.value += Math.random() * 15;

    if (loadingProgress.value > 88) {
      clearInterval(intervalId);
    }
  }, 300);

  try {
    contents = await promise;
  } catch {
    // TODO handle errors
  }

  loadingProgress.value = 100;
  clearInterval(intervalId);

  setTimeout(() => {
    data.value = {
      ...data.value,
      ...(contents ?? {}),
    };
    loadingProgress.value = 0;
  }, 300);
}

function onTagsFilter(tags: Tag[]) {
  availableTags.value = tags;

  const contents = Object.entries(data.value).reduce((data, [key, filters]) => {
    if (filters.visible) {
      const filter = new Filter(
        key as CategoryType,
        {
          ...filters.payload,
          page: 0,
          tags: tags.filter((tag) => tag.selected).map((tag) => tag.name),
        },
        filters.visible
      );
      data[key as CategoryType] = filter;
    }
    return data;
  }, {} as FiltersType);

  onFilterImages(fetchImages(contents));
}

const onSearchChange = debounce(async () => {
  const contents = Object.entries(data.value).reduce((data, [key, filters]) => {
    if (filters.visible) {
      const filter = new Filter(
        key as CategoryType,
        {
          ...filters.payload,
          querySearch: search.value || undefined,
          page: 0,
        },
        filters.visible
      );
      data[key as CategoryType] = filter;
    }
    return data;
  }, {} as FiltersType);

  onFilterImages(fetchImages(contents));
});

async function setAvailableTags() {
  const settings = await api.mediaManager.getSettings({ category: MediaManager.Mode.IMAGES });
  availableTags.value = settings.uniqueTags.map((item) => ({
    name: item,
    selected: false,
  }));
}

onBeforeMount(async () => {
  try {
    isLoadingData.value = true;
    initializeFilters();
    await Promise.allSettled([setAvailableTags(), fetchImages(data.value)]);
  } finally {
    isLoadingData.value = false;
  }
});

function open(editMedia: ImageCreative[] = []) {
  isModalOpen.value = true;
  Object.values(data.value).forEach(
    (item) =>
      (item.images = item.images.map((img) => {
        if (imageIdsFromStore.value.includes(img.id)) {
          return {
            ...img,
            selected: false,
            message: {
              type: BaseMessageType.ERROR,
              icon: 'errorCircle',
              content: 'Image is already inserted.',
              height: 20,
              placement: Position.RIGHT_BOTTOM,
              positionX: Position.RIGHT,
              positionY: Position.BOTTOM,
              color: 'red',
            },
          };
        }

        return { ...img, selected: false };
      }))
  );

  if (editMedia.length) {
    activeStep.value = Step.EDIT_MEDIA;
    imagesToEdit.value = editMedia;
  }
}

function close() {
  isModalOpen.value = false;
  imagesToEdit.value = null;
  activeStep.value = Step.SELECT_MEDIA;
}

async function addFiles(files: FileList) {
  try {
    isImageUploading.value = true;

    const imagesData = await MediaManagerHelpers.getImageData(files, true);

    const results = await Promise.allSettled(
      imagesData.map((file) =>
        api.mediaManager.createNewData(MediaManager.Mode.IMAGES, {
          tags: [],
          file,
        })
      )
    );

    const mappedResults = results.reduce(
      (mappedResults: { images: Image[]; errors: any[] }, item) => {
        if (item.status === 'fulfilled') {
          const image = item.value.data;

          mappedResults.images.push({
            image_url: image.image_url,
            size: image.image_size,
            width: image.image_width,
            height: image.image_height,
            category: CategoryType.UPLOADED,
            selected: true,
            id: image.id,
          });
        }
        return mappedResults;
      },
      { images: [], errors: [] }
    );

    const categories = [CategoryType.ALL, CategoryType.UPLOADED];

    categories.forEach((item) => {
      data.value[item].images.unshift(...cloneDeep(mappedResults.images));
    });

    if (!categories.includes(activeCategory.value)) {
      activeCategory.value = CategoryType.UPLOADED;
    }

    if (mappedResults.errors.length) {
      addImageError.value = `We couldn't upload ${mappedResults.errors.length} ${pluralize(
        mappedResults.errors.length,
        'image'
      )}. Please try again or contact support.`;
    }
  } catch {
    addImageError.value = 'We could not insert image. Please try again.';
  } finally {
    isImageUploading.value = false;
  }
}

function setCropImage(image: Image, aspectRatio: SupportedCrop, isActive: boolean) {
  if (isActive) {
    cropImage.value = {
      ...image,
      aspectRatio: availablePlacementAspectRatios[aspectRatio]!,
    };
  } else {
    data.value[activeCategory.value].images = images.value.map((image) => {
      if (image.cropVariations && aspectRatio && image.cropVariations[aspectRatio]) {
        image.cropVariations[aspectRatio].useCrop = false;
      }
      return image;
    });
  }
}

function patchImage(value: Partial<ImageCreative>) {
  const contents = images.value.map((image) => (image.id === value.id ? { ...image, ...value } : image));

  if (imagesToEdit.value) {
    imagesToEdit.value = contents;
  } else {
    data.value[activeCategory.value].images = contents;
  }
}

function onImageEditSave(value: ImageCreative) {
  patchImage(value);
  editImage.value = null;
}

function onImageCropSave(value: ImageCreative) {
  patchImage(value);
  cropImage.value = null;
}

function onImageEditCancel() {
  editImage.value = null;
}

function onImageCropCancel() {
  data.value[activeCategory.value].images = images.value.map((image) => {
    const aspectRatio = cropImage.value?.aspectRatio.value;

    if (image.cropVariations && aspectRatio && image.cropVariations[aspectRatio]) {
      image.cropVariations[aspectRatio].useCrop = image.image_url === cropImage.value?.image_url;
    }
    return image;
  });
  cropImage.value = null;
}

function onCategoryChange(filter: CategoryType) {
  activeCategory.value = filter;
}

function onFilterUpdate(value: ViewLevelActionsEvent) {
  secondaryCategories.value = secondaryCategories.value.map((group) => {
    const isWithinGroup = !!group.find((action) => action.event === value);

    return group.map((action) => ({
      ...action,
      active: (action.event === value && !action.active) || (action.active && !isWithinGroup),
    }));
  });
}

function onImageInputChange(event: Event) {
  const { files } = event.target as HTMLInputElement;

  if (files) {
    addFiles(files);
  }
  hiddenInputFile.value!.value = '';
}

function onHiddenInputFileClick() {
  hiddenInputFile.value?.click();
}

function onSelectToggle(image: Image) {
  data.value[activeCategory.value].images = images.value.map((item) => {
    if (item.id === image.id) {
      item.selected = !item.selected;
    }
    return item;
  });
}

function onImageError(image: Image) {
  if (imagesToEdit.value) {
    return;
  }

  data.value[activeCategory.value].images = images.value.map((item) => ({
    ...item,
    ...(item.id === image.id && {
      message: {
        type: BaseMessageType.ERROR,
        icon: 'errorCircle',
        content: 'Image could not be loaded.',
        height: 20,
        placement: Position.RIGHT_BOTTOM,
        positionX: Position.RIGHT,
        positionY: Position.BOTTOM,
        color: 'red',
      },
    }),
  }));
}

function onCancelClick() {
  switch (activeStep.value) {
    case Step.SELECT_MEDIA:
      close();
      break;
    case Step.EDIT_MEDIA:
      if (imagesToEdit.value) {
        close();
      } else {
        activeStep.value = Step.SELECT_MEDIA;
      }
      break;
  }
}

function onSaveClick() {
  switch (activeStep.value) {
    case Step.SELECT_MEDIA:
      activeStep.value = Step.EDIT_MEDIA;
      break;
    case Step.EDIT_MEDIA:
      emits(
        'onAddImage',
        selectedImages.value.map((item) => ({
          id: item.id,
          image_url: item.image_url,
          width: item.width,
          height: item.height,
          size: item.size,
          cropVariations: item.cropVariations,
        }))
      );
      close();
      break;
  }
}

expose({
  open,
  close,
});

return { __sfc: true,emits, adAccountStore, adAccountGetters, isModalOpen, isLoadingData, isImageUploading, isLoadingMore, hiddenInputFile, activeStep, search, availableTags, secondaryCategories, addImageError, cropImage, editImage, imagesToEdit, activeCategory, loadingProgress, data, creativeStore, creativeGetters, imageIdsFromStore, availableCategories, images, selectedImages, getPlacementKey, modalButtons, noDataMessage, initializeFilters, onPageChange, handleImagesScroll, onFilterImages, onTagsFilter, onSearchChange, setAvailableTags, open, close, addFiles, setCropImage, patchImage, onImageEditSave, onImageCropSave, onImageEditCancel, onImageCropCancel, onCategoryChange, onFilterUpdate, onImageInputChange, onHiddenInputFileClick, onSelectToggle, onImageError, onCancelClick, onSaveClick, CcAlert, CcIcon, CcImageCard, CcImagePlacementCrop, CcSecondaryFilter, CcTagsFilter, LoadingSkeleton, availablePlacementAspectRatios, Step, CcCropImageModal, CcEditImageModal, CcModal, SupportedCrop, NoData, Position }
}

})